import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';

const API_BASE = 'https://api.starviewcinema.com';

const COUNTRIES = [
  'USA', 'INDIA', 'CHINA', 'AUSTRALIA', 'UK', 'GERMANY',
  'JAPAN', 'KOREA', 'MEXICO', 'AFRICA', 'FRANCE', 'UKRAINE',
];

const SECRET_STORAGE_KEY = 'starviewcinema_admin_secret';

async function fetchMoviesForCountry(country) {
  const res = await fetch(`${API_BASE}/api/movies?country=${encodeURIComponent(country)}`);
  if (!res.ok) throw new Error(`Failed to load movies for ${country} (${res.status})`);
  const data = await res.json();
  return (data.movies || []).sort((a, b) => (a.theaterNumber || 0) - (b.theaterNumber || 0));
}

async function saveMovie(movieId, updates, secret) {
  const res = await fetch(`${API_BASE}/api/admin/movies/${movieId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'x-admin-secret': secret,
    },
    body: JSON.stringify(updates),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || `Save failed (${res.status})`);
  return data;
}

// Real title search against TMDB (the official movie database API), not a
// scraper - see tmdbService.ts on the gateway for why.
async function searchTmdb(title, secret) {
  const res = await fetch(`${API_BASE}/api/admin/tmdb/search?title=${encodeURIComponent(title)}`, {
    headers: { 'x-admin-secret': secret },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || `TMDB search failed (${res.status})`);
  return data.candidates || [];
}

async function getTmdbDetails(tmdbId, secret) {
  const res = await fetch(`${API_BASE}/api/admin/tmdb/details/${tmdbId}`, {
    headers: { 'x-admin-secret': secret },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || `TMDB lookup failed (${res.status})`);
  return data.details;
}

// Real, currently-in-theaters listing for one country, with titles already
// localized into that country's real language by TMDB itself.
async function getTmdbNowPlaying(country, secret) {
  const res = await fetch(`${API_BASE}/api/admin/tmdb/now-playing?country=${encodeURIComponent(country)}`, {
    headers: { 'x-admin-secret': secret },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || `TMDB now-playing lookup failed (${res.status})`);
  return data.nowPlaying || [];
}

// Real, self-hosted trailer upload - no YouTube/Vimeo embed, no third-party
// branding. The file is stored on the gateway server and served back from
// its own domain (see uploadsController.ts).
async function uploadTrailerFile(file, secret) {
  const formData = new FormData();
  formData.append('trailer', file);
  const res = await fetch(`${API_BASE}/api/admin/upload/trailer`, {
    method: 'POST',
    headers: { 'x-admin-secret': secret },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || `Upload failed (${res.status})`);
  return data.url;
}

const MovieCard = forwardRef(function MovieCard({ movie, secret, onSaved }, ref) {
  const [title, setTitle] = useState(movie.movieTitle || '');
  const [image, setImage] = useState(movie.movieImage || '');
  const [trailer, setTrailer] = useState(movie.trailerUrl || '');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  const [tmdbSearching, setTmdbSearching] = useState(false);
  const [tmdbCandidates, setTmdbCandidates] = useState(null);
  const [tmdbApplyingId, setTmdbApplyingId] = useState(null);
  const [tmdbError, setTmdbError] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Lets the parent (the "Now Playing in {country}" panel) push a real,
  // localized title - and optionally a poster - straight into this specific
  // theater slot's fields, without lifting title/image state up globally.
  useImperativeHandle(ref, () => ({
    applyNowPlaying: (localTitle, posterUrl) => {
      setTitle(localTitle);
      if (posterUrl) setImage(posterUrl);
      setStatus(null);
    },
  }));

  const dirty = title !== (movie.movieTitle || '') || image !== (movie.movieImage || '') || trailer !== (movie.trailerUrl || '');

  const handleTrailerFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const url = await uploadTrailerFile(file, secret);
      setTrailer(url);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await saveMovie(movie._id, { movieTitle: title, movieImage: image, trailerUrl: trailer }, secret);
      setStatus({ ok: true, message: 'Saved' });
      onSaved({ ...movie, movieTitle: title, movieImage: image, trailerUrl: trailer });
    } catch (err) {
      setStatus({ ok: false, message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const applyCandidate = async (candidate) => {
    setTmdbApplyingId(candidate.tmdbId);
    setTmdbError(null);
    try {
      const details = await getTmdbDetails(candidate.tmdbId, secret);
      // Poster only - TMDB's trailer is always a YouTube link, and this
      // site shows zero third-party branding. Trailers come from the real
      // uploaded file below instead.
      if (details.posterUrl) setImage(details.posterUrl);
      setTmdbCandidates(null);
      setStatus(null);
    } catch (err) {
      setTmdbError(err.message);
    } finally {
      setTmdbApplyingId(null);
    }
  };

  const handleTmdbSearch = async () => {
    if (!title.trim()) return;
    setTmdbSearching(true);
    setTmdbError(null);
    setTmdbCandidates(null);
    try {
      const candidates = await searchTmdb(title, secret);
      if (candidates.length === 0) {
        setTmdbError('No TMDB match found for that title.');
      } else if (candidates.length === 1) {
        await applyCandidate(candidates[0]);
      } else {
        setTmdbCandidates(candidates);
      }
    } catch (err) {
      setTmdbError(err.message);
    } finally {
      setTmdbSearching(false);
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <span style={styles.theaterBadge}>Theater {movie.theaterNumber}</span>
        {status && (
          <span style={{ color: status.ok ? '#4caf50' : '#e53935', fontSize: 13 }}>
            {status.message}
          </span>
        )}
      </div>
      <div style={styles.cardBody}>
        {image ? (
          <img src={image} alt={title} style={styles.poster} onError={(e) => { e.target.style.opacity = 0.3; }} />
        ) : (
          <div style={{ ...styles.poster, ...styles.posterEmpty }}>No image</div>
        )}
        <div style={styles.fields}>
          <label style={styles.label}>Title</label>
          <input style={styles.input} value={title} onChange={(e) => setTitle(e.target.value)} />

          <label style={styles.label}>Poster image URL</label>
          <input style={styles.input} value={image} onChange={(e) => setImage(e.target.value)} />

          <label style={styles.label}>Trailer video URL</label>
          <input style={styles.input} value={trailer} onChange={(e) => setTrailer(e.target.value)} />

          {trailer && (
            <video key={trailer} src={trailer} controls style={styles.trailerPreview} />
          )}

          <label style={{ ...styles.uploadLabel, opacity: uploading ? 0.6 : 1, cursor: uploading ? 'not-allowed' : 'pointer' }}>
            {uploading ? 'Uploading…' : '📤 Upload real trailer file (.mp4, .mov, .m4v, .webm)'}
            <input
              type="file"
              accept="video/mp4,video/quicktime,video/webm,video/x-m4v,.mp4,.mov,.m4v,.webm"
              style={styles.uploadInput}
              onChange={handleTrailerFileChange}
              disabled={uploading}
            />
          </label>
          {uploadError && <span style={styles.tmdbError}>{uploadError}</span>}

          <button
            style={{ ...styles.tmdbButton, opacity: title.trim() && !tmdbSearching ? 1 : 0.5 }}
            disabled={!title.trim() || tmdbSearching}
            onClick={handleTmdbSearch}
          >
            {tmdbSearching ? 'Searching TMDB…' : '🎬 Auto-fill poster from TMDB'}
          </button>
          {tmdbError && <span style={styles.tmdbError}>{tmdbError}</span>}

          {tmdbCandidates && (
            <div style={styles.tmdbPicker}>
              <p style={styles.tmdbPickerLabel}>Multiple matches — pick the right one:</p>
              {tmdbCandidates.map((c) => (
                <button
                  key={c.tmdbId}
                  style={styles.tmdbCandidate}
                  onClick={() => applyCandidate(c)}
                  disabled={tmdbApplyingId === c.tmdbId}
                >
                  {c.posterThumbUrl ? (
                    <img src={c.posterThumbUrl} alt={c.title} style={styles.tmdbCandidateThumb} />
                  ) : (
                    <div style={{ ...styles.tmdbCandidateThumb, ...styles.posterEmpty }} />
                  )}
                  <span style={styles.tmdbCandidateText}>
                    {c.title} {c.year ? `(${c.year})` : ''}
                    {tmdbApplyingId === c.tmdbId && ' — applying…'}
                  </span>
                </button>
              ))}
            </div>
          )}

          <button
            style={{ ...styles.saveButton, opacity: dirty && !saving ? 1 : 0.5 }}
            disabled={!dirty || saving}
            onClick={handleSave}
          >
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
});

export default function MovieCrawlerDashboard() {
  const [secret, setSecret] = useState(() => sessionStorage.getItem(SECRET_STORAGE_KEY) || '');
  const [secretInput, setSecretInput] = useState('');
  const [country, setCountry] = useState('USA');
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cardRefs = useRef({});

  const [nowPlaying, setNowPlaying] = useState(null);
  const [nowPlayingLoading, setNowPlayingLoading] = useState(false);
  const [nowPlayingError, setNowPlayingError] = useState(null);
  const [assignTheater, setAssignTheater] = useState(1);
  const [assigningKey, setAssigningKey] = useState(null);

  const loadCountry = useCallback((c) => {
    setLoading(true);
    setError(null);
    fetchMoviesForCountry(c)
      .then(setMovies)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (secret) loadCountry(country);
    setNowPlaying(null);
    setNowPlayingError(null);
  }, [country, secret, loadCountry]);

  const handleUnlock = () => {
    sessionStorage.setItem(SECRET_STORAGE_KEY, secretInput);
    setSecret(secretInput);
  };

  const handleSaved = (updatedMovie) => {
    setMovies((prev) => prev.map((m) => (m._id === updatedMovie._id ? updatedMovie : m)));
  };

  const handleLoadNowPlaying = async () => {
    setNowPlayingLoading(true);
    setNowPlayingError(null);
    try {
      const results = await getTmdbNowPlaying(country, secret);
      setNowPlaying(results);
    } catch (err) {
      setNowPlayingError(err.message);
    } finally {
      setNowPlayingLoading(false);
    }
  };

  const handleAssign = async (candidate) => {
    const targetMovie = movies.find((m) => m.theaterNumber === assignTheater);
    if (!targetMovie) {
      setNowPlayingError(`No theater ${assignTheater} loaded for ${country} - refresh the country first.`);
      return;
    }
    const cardHandle = cardRefs.current[targetMovie._id];
    if (!cardHandle) return;
    setAssigningKey(candidate.tmdbId);
    setNowPlayingError(null);
    try {
      // Full-resolution poster via the details lookup - the now-playing
      // listing only carries a small thumbnail.
      const details = await getTmdbDetails(candidate.tmdbId, secret);
      cardHandle.applyNowPlaying(candidate.title, details.posterUrl);
    } catch (err) {
      setNowPlayingError(err.message);
    } finally {
      setAssigningKey(null);
    }
  };

  if (!secret) {
    return (
      <div style={styles.gate}>
        <h2>Admin Access</h2>
        <p>Enter the admin secret to manage movies.</p>
        <input
          type="password"
          style={styles.input}
          value={secretInput}
          onChange={(e) => setSecretInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
          placeholder="Admin secret"
        />
        <button style={styles.saveButton} onClick={handleUnlock}>Unlock</button>
      </div>
    );
  }

  return (
    <div style={{ padding: 32, fontFamily: 'sans-serif' }}>
      <h1>Movie Admin Panel</h1>
      <p style={{ color: '#666' }}>
        Edit the title, poster image, and trailer for each of the 7 theater slots per country.
        Changes save directly to the live movie service and take effect immediately in the app.
      </p>

      <div style={{ margin: '16px 0' }}>
        <label style={{ marginRight: 8, fontWeight: 'bold' }}>Country:</label>
        <select value={country} onChange={(e) => setCountry(e.target.value)} style={styles.select}>
          {COUNTRIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <button style={{ ...styles.saveButton, marginLeft: 12 }} onClick={() => loadCountry(country)} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && <div style={{ color: '#e53935', marginBottom: 16 }}>{error}</div>}

      <div style={styles.nowPlayingPanel}>
        <div style={styles.nowPlayingHeader}>
          <div>
            <h3 style={{ margin: '0 0 4px' }}>🌍 What's Playing in {country} Right Now</h3>
            <p style={{ margin: 0, fontSize: 12.5, color: '#666' }}>
              Real theatrical listings from TMDB, titles already localized into {country}'s real language - accurate and guaranteed to match back to the right TMDB entry.
            </p>
          </div>
          <button style={styles.tmdbButton} onClick={handleLoadNowPlaying} disabled={nowPlayingLoading}>
            {nowPlayingLoading ? 'Loading…' : nowPlaying ? 'Refresh' : 'Load Now Playing'}
          </button>
        </div>

        {nowPlayingError && <div style={{ color: '#e53935', fontSize: 13, marginTop: 8 }}>{nowPlayingError}</div>}

        {nowPlaying && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0' }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Assign to Theater:</label>
              <select value={assignTheater} onChange={(e) => setAssignTheater(Number(e.target.value))} style={styles.select}>
                {(movies.length ? movies.map((m) => m.theaterNumber) : [1, 2, 3, 4, 5, 6, 7]).map((n) => (
                  <option key={n} value={n}>Theater {n}</option>
                ))}
              </select>
            </div>

            {nowPlaying.length === 0 ? (
              <p style={{ fontSize: 13, color: '#666' }}>No current theatrical listings found for {country}.</p>
            ) : (
              <div style={styles.nowPlayingGrid}>
                {nowPlaying.map((c) => (
                  <div key={c.tmdbId} style={styles.nowPlayingCard}>
                    {c.posterThumbUrl ? (
                      <img src={c.posterThumbUrl} alt={c.title} style={styles.nowPlayingThumb} />
                    ) : (
                      <div style={{ ...styles.nowPlayingThumb, ...styles.posterEmpty }} />
                    )}
                    <div style={styles.nowPlayingInfo}>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{c.title}</div>
                      {c.originalTitle !== c.title && (
                        <div style={{ fontSize: 11, color: '#888' }}>orig: {c.originalTitle}</div>
                      )}
                      {c.releaseDate && <div style={{ fontSize: 11, color: '#888' }}>{c.releaseDate}</div>}
                      <button
                        style={{ ...styles.tmdbButton, marginTop: 6, fontSize: 12, padding: '5px 10px' }}
                        onClick={() => handleAssign(c)}
                        disabled={assigningKey === c.tmdbId}
                      >
                        {assigningKey === c.tmdbId ? 'Assigning…' : `→ Theater ${assignTheater}`}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {!loading && movies.length === 0 && !error && <p>No movies found for {country}.</p>}

      <div style={styles.grid}>
        {movies.map((movie) => (
          <MovieCard
            key={movie._id}
            ref={(el) => { cardRefs.current[movie._id] = el; }}
            movie={movie}
            secret={secret}
            onSaved={handleSaved}
          />
        ))}
      </div>
    </div>
  );
}

const styles = {
  gate: {
    maxWidth: 400,
    margin: '80px auto',
    padding: 32,
    textAlign: 'center',
    fontFamily: 'sans-serif',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: 20,
  },
  card: {
    border: '1px solid #ddd',
    borderRadius: 8,
    overflow: 'hidden',
    background: '#fff',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    background: '#f5f5f5',
    borderBottom: '1px solid #eee',
  },
  theaterBadge: {
    fontWeight: 'bold',
    fontSize: 13,
  },
  cardBody: {
    display: 'flex',
    padding: 12,
    gap: 12,
  },
  poster: {
    width: 90,
    height: 135,
    objectFit: 'cover',
    borderRadius: 4,
    flexShrink: 0,
    background: '#eee',
  },
  posterEmpty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    color: '#999',
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gap: 4,
  },
  label: {
    fontSize: 12,
    color: '#666',
  },
  input: {
    padding: '6px 8px',
    fontSize: 13,
    border: '1px solid #ccc',
    borderRadius: 4,
    marginBottom: 6,
  },
  select: {
    padding: '6px 8px',
    fontSize: 14,
  },
  saveButton: {
    marginTop: 4,
    padding: '8px 14px',
    background: '#1565c0',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
  },
  tmdbButton: {
    marginTop: 4,
    padding: '8px 14px',
    background: '#8A2BE2',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
  },
  tmdbError: {
    fontSize: 12,
    color: '#e53935',
    marginTop: 4,
  },
  tmdbPicker: {
    marginTop: 8,
    border: '1px solid #eee',
    borderRadius: 6,
    padding: 8,
    background: '#fafafa',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  tmdbPickerLabel: {
    fontSize: 12,
    color: '#666',
    margin: '0 0 4px',
  },
  tmdbCandidate: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 6,
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: 4,
    cursor: 'pointer',
    textAlign: 'left',
  },
  tmdbCandidateThumb: {
    width: 32,
    height: 48,
    objectFit: 'cover',
    borderRadius: 3,
    flexShrink: 0,
    background: '#eee',
  },
  tmdbCandidateText: {
    fontSize: 12.5,
    color: '#222',
  },
  trailerPreview: {
    width: '100%',
    maxHeight: 160,
    borderRadius: 4,
    background: '#000',
    marginBottom: 6,
  },
  uploadLabel: {
    display: 'block',
    marginTop: 4,
    marginBottom: 4,
    padding: '8px 14px',
    background: '#2e7d32',
    color: '#fff',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'center',
  },
  uploadInput: {
    display: 'none',
  },
  nowPlayingPanel: {
    border: '1px solid #ddd',
    borderRadius: 8,
    padding: 16,
    background: '#f8f9fb',
    marginBottom: 24,
  },
  nowPlayingHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    flexWrap: 'wrap',
  },
  nowPlayingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 12,
  },
  nowPlayingCard: {
    display: 'flex',
    gap: 10,
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    padding: 8,
  },
  nowPlayingThumb: {
    width: 54,
    height: 80,
    objectFit: 'cover',
    borderRadius: 4,
    flexShrink: 0,
    background: '#eee',
  },
  nowPlayingInfo: {
    flex: 1,
    minWidth: 0,
  },
};
